import { getLocalTimeZone, today } from '@internationalized/date'
import { addMonths, differenceInCalendarMonths, format, parseISO } from 'date-fns'

import { type IGerenciadorIntermediarias } from '@/domain/simulador/gerenciador-intermediarias.interface'
import { PeriodicidadePagamentoEnum } from '@/types/simulacao'
import type { TPeriodicidadePagamentoEnum } from '@/types/simulacao'
import type { TGrupoIntermediarias } from '@/utils/calculadora-valores-totais/processador-valores-decimal'

export class GerenciadorIntermediariasBase implements IGerenciadorIntermediarias<TGrupoIntermediarias> {
  protected static readonly valorDaPeriodicidade: Record<TPeriodicidadePagamentoEnum, number> = {
    Anual: 12,
    Semestral: 6,
    Trimestral: 3,
    Bimestral: 2,
    SemPeriodoFixo: 0,
  }

  private static readonly periodicidadePorMeses = new Map<number, TPeriodicidadePagamentoEnum>([
    [12, 'Anual'],
    [6, 'Semestral'],
    [3, 'Trimestral'],
    [2, 'Bimestral'],
  ])

  protected calcularPeriodicidade(datas: string[]): TPeriodicidadePagamentoEnum {
    if (!datas || datas.length < 2) return 'SemPeriodoFixo'
    const [data1, data2] = datas
    if (!data1 || !data2) return 'SemPeriodoFixo'
    const primeira = parseISO(data1)
    const segunda = parseISO(data2)
    const diferencaMeses = Math.abs(differenceInCalendarMonths(primeira, segunda))
    return GerenciadorIntermediariasBase.periodicidadePorMeses.get(diferencaMeses) ?? 'SemPeriodoFixo'
  }

  criarGrupoInicial(
    datas: string[],
    valor: number = 0,
    _dataPrevisaoDeEntrega?: string,
    periodicidadeFixa?: TPeriodicidadePagamentoEnum,
  ): TGrupoIntermediarias[] {
    if (!datas?.length || !valor) return []
    const periodicidade = periodicidadeFixa ?? this.calcularPeriodicidade(datas)
    return [
      {
        parcelas: datas.map((data) => ({ data })),
        periodicidade,
        valor,
      },
    ]
  }

  criarProximoGrupo(
    dataUltimaParcela?: string | null,
    periodicidade: TPeriodicidadePagamentoEnum = PeriodicidadePagamentoEnum.SemPeriodoFixo,
    _dataPrevisaoDeEntrega?: string,
  ): TGrupoIntermediarias {
    const hoje = today(getLocalTimeZone()).toString()

    if (!dataUltimaParcela) {
      return {
        valor: 0,
        periodicidade,
        parcelas: [{ data: hoje }],
      }
    }

    const mesesParaAdicionar =
      periodicidade === 'SemPeriodoFixo' ? 1 : GerenciadorIntermediariasBase.valorDaPeriodicidade[periodicidade]

    const proximaData = format(addMonths(parseISO(dataUltimaParcela), mesesParaAdicionar), 'yyyy-MM-dd')
    return {
      valor: 0,
      periodicidade,
      parcelas: [{ data: proximaData }],
    }
  }

  calcularAjustePorQuantidade(grupo: TGrupoIntermediarias, novaQuantidade: number): TGrupoIntermediarias {
    const quantidadeAtual = grupo.parcelas.length
    if (quantidadeAtual === 0) return grupo

    const intervalo = GerenciadorIntermediariasBase.valorDaPeriodicidade[grupo.periodicidade]

    if (novaQuantidade > quantidadeAtual) {
      const ultimaParcela = grupo.parcelas[quantidadeAtual - 1]
      if (!ultimaParcela?.data) return { ...grupo, parcelas: [...grupo.parcelas, { data: null }] }

      const ultimaData = parseISO(ultimaParcela.data)
      const mesesParaAdicionar = intervalo || 1

      const novasParcelasAdicionais = Array.from({ length: novaQuantidade - quantidadeAtual }, (_, i) => ({
        data: format(addMonths(ultimaData, (i + 1) * mesesParaAdicionar), 'yyyy-MM-dd'),
      }))

      return {
        ...grupo,
        parcelas: [...grupo.parcelas, ...novasParcelasAdicionais],
      }
    }

    const novaPeriodicidade = novaQuantidade === 1 ? PeriodicidadePagamentoEnum.SemPeriodoFixo : grupo.periodicidade
    return {
      ...grupo,
      periodicidade: novaPeriodicidade,
      parcelas: grupo.parcelas.slice(0, novaQuantidade),
    }
  }

  recalcularDatas(
    grupo: TGrupoIntermediarias,
    dataModificada: string,
    periodicidade: TPeriodicidadePagamentoEnum,
  ): TGrupoIntermediarias {
    if (periodicidade === 'SemPeriodoFixo') return grupo
    const intervalo = GerenciadorIntermediariasBase.valorDaPeriodicidade[periodicidade]
    const parcelasRecalculadas = grupo.parcelas.map((parcela, index) => ({
      ...parcela,
      data: format(addMonths(parseISO(dataModificada), intervalo * index), 'yyyy-MM-dd'),
    }))

    return {
      ...grupo,
      parcelas: parcelasRecalculadas,
    }
  }
}
